/**
 * @file constants
 * @author alfielv
 */

import _ from 'lodash';
import { AI_ASSISTANT_REPORT_EVENT_NAME } from '../../../constants';

import type { KeyEventItem } from './types';

// 事件类型
export const ATTENTION_EVENT_TYPE = {
  /** 效果异常 */
  EFFECT_EXCEPTION: 1,
  /** 新品助推 */
  NEW_PRODUCT_PROMOTION: 2,
  /** 财务异常 */
  FINANCE_EXCEPTION: 3,
  /** 风控违规 */
  POLICY_VIOLATION: 4,
  /** 营销机会 */
  MARKETING_OPPORTUNITY: 5,
  /** 账户超限额红线 */
  ACCOUNT_RED_LINE: 6,
  /** 账户结构异常,包含中度和重度 */
  ACCOUNT_STRUCTURE_ABNORMAL: 7,
  /** 预算分配不合理 */
  ACCOUNT_BUDGET_ALLOCATION_IRRATIONAL: 8,
  /** 预算即将超限 */
  ACCOUNT_BUDGET_ALLOCATION_NEAR_LIMIT: 9,
  /** 消耗波动异常 */
  ACCOUNT_COST_FLUCTUATION: 10,
  /** 转化成本异常 */
  ACCOUNT_CONVERSION_COST_ABNORMAL: 11,
  /** 行业热门创意洞察 */
  INDUSTRY_HOT_CREATIVE_INSIGHTS: 12,
  /** 账户治理超限额 */
  ACCOUNT_GOVERNANCE_EXCEED_QUOTA: 13,
  /** 账户治理预警 */
  ACCOUNT_GOVERNANCE_WARNING: 14,
};

// 关联主体类型
export const AD_CUSTOMER_TYPE = {
  /** 账号受限 */
  ACCOUNT_LIMIT: 'ACCOUNT_LIMIT',
  /** 账号预警 */
  ACCOUNT_WARNING: 'ACCOUNT_WARNING',
};

export const formatCount = (count: number) => {
  if (count >= 100) {
    return '100+';
  }
  return count;
};

// 事件信息
export const ATTENTION_EVENT_INFO = {
  // 花费异常
  [ATTENTION_EVENT_TYPE.EFFECT_EXCEPTION]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${eventData.count} 个推广产品花费异常`,
    desc: '建议尽快定位异动原因',
    actionText: '查看详情',
    tagText: '异动监控',
    defaultPrompt: '扫描账户花费异常情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.EFFECT_EXCEPTION}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.EFFECT_EXCEPTION}`,
    // enable: (eventData: KeyEventItem) => eventData.count > 0,
    enable: () => false,
  },
  // 新品助推
  [ATTENTION_EVENT_TYPE.NEW_PRODUCT_PROMOTION]: {
    getType: () => 'info',
    title: (eventData: KeyEventItem) => `${eventData.count} 个新品有助推策略`,
    desc: '调整创意/出价快速起量',
    actionText: '查看详情',
    tagText: '平台策略',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.NEW_PRODUCT_PROMOTION}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.NEW_PRODUCT_PROMOTION}`,
    // enable: (eventData: KeyEventItem) => eventData.count > 0,
    enable: () => false,
  },
  // 财务异常
  [ATTENTION_EVENT_TYPE.FINANCE_EXCEPTION]: {
    getType: () => 'error',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户即将余额不足`,
    desc: '资金余额即将不足会影响投放，请及时充值',
    actionText: '查看详情',
    tagText: '异动监控',
    defaultPrompt: '扫描账户余额不足情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.FINANCE_EXCEPTION}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.FINANCE_EXCEPTION}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // TODO.风控违规
  [ATTENTION_EVENT_TYPE.POLICY_VIOLATION]: {
    getType: () => 'error',
    title: (eventData: KeyEventItem) => `${eventData.count} 个主体/账户违规封停`,
    desc: '建议及时解决避免影响投放',
    actionText: '查看详情',
    tagText: '平台策略',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.POLICY_VIOLATION}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.POLICY_VIOLATION}`,
    enable: () => false,
  },
  // TODO.营销机会
  [ATTENTION_EVENT_TYPE.MARKETING_OPPORTUNITY]: {
    getType: () => 'info',
    title: (eventData: KeyEventItem) => `${eventData.count} 个行业推荐新品`,
    desc: '建议及时关注',
    actionText: '查看详情',
    tagText: '经营机会',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.MARKETING_OPPORTUNITY}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.MARKETING_OPPORTUNITY}`,
    enable: () => false,
  },
  // 账户超限额红线
  [ATTENTION_EVENT_TYPE.ACCOUNT_RED_LINE]: {
    getType: (eventData: KeyEventItem) => {
      // 受限账户数
      const limitAccountsNumber = _.filter(eventData.customer_list, { ad_customer_type: AD_CUSTOMER_TYPE.ACCOUNT_LIMIT })?.length;
      // 预警账户数
      const warningAccountsNumber = _.filter(eventData.customer_list, { ad_customer_type: AD_CUSTOMER_TYPE.ACCOUNT_WARNING })?.length;
      // 全部/部分 受限
      if (limitAccountsNumber > 0) {
        return 'error';
      }
      // 全部/部分 预警
      if (warningAccountsNumber > 0) {
        return 'warning';
      }
      // 兜底
      return 'error';
    },
    title: (eventData: KeyEventItem) => {
      const totalAccountsNumber = eventData.customer_list?.length;
      // 受限账户数
      const limitAccountsNumber = _.filter(eventData.customer_list, { ad_customer_type: AD_CUSTOMER_TYPE.ACCOUNT_LIMIT })?.length;
      // 预警账户数
      const warningAccountsNumber = _.filter(eventData.customer_list, { ad_customer_type: AD_CUSTOMER_TYPE.ACCOUNT_WARNING })?.length;
      // 全部/部分 受限
      if (limitAccountsNumber > 0) {
        return `${limitAccountsNumber} 个主体触达可投账户数限制`;
      }
      // 全部/部分 预警
      if (warningAccountsNumber > 0) {
        return `${warningAccountsNumber} 个主体将触达可投账户数限制`;
      }
      // 兜底
      return `${totalAccountsNumber} 个主体触达可投账户数限制`;
    },
    desc: '信誉等级偏低请即时关注',
    actionText: '查看详情',
    tagText: '异动监控',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_RED_LINE}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_RED_LINE}`,
    // enable: (eventData: KeyEventItem) => eventData.customer_list?.length > 0,
    enable: () => false,
  },
  // 账户结构异常,包含中度和重度
  [ATTENTION_EVENT_TYPE.ACCOUNT_STRUCTURE_ABNORMAL]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户结构异常`,
    desc: '低效营销单元和空耗营销单元占比过高，请及时清理',
    actionText: '查看详情',
    tagText: '平台策略',
    defaultPrompt: '扫描账户结构异常情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_STRUCTURE_ABNORMAL}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_STRUCTURE_ABNORMAL}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 预算分配不合理
  [ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_IRRATIONAL]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户预算分配不合理`,
    desc: '预算分配远低于同行水平，请及时调整预算',
    actionText: '查看详情',
    tagText: '平台策略',
    defaultPrompt: '扫描账户预算分配不合理情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_IRRATIONAL}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_IRRATIONAL}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 预算即将超限
  [ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_NEAR_LIMIT]: {
    getType: () => 'error',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户预算即将超限`,
    desc: '预算超限会影响投放，请及时调整预算',
    actionText: '查看详情',
    tagText: '平台策略',
    defaultPrompt: '扫描账户预算即将超限情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_NEAR_LIMIT}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_BUDGET_ALLOCATION_NEAR_LIMIT}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 消耗波动异常
  [ATTENTION_EVENT_TYPE.ACCOUNT_COST_FLUCTUATION]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户消耗波动异常`,
    desc: '存在多类因素导致花费波动异常，请及时关注',
    actionText: '查看详情',
    tagText: '平台策略',
    defaultPrompt: '扫描账户消耗波动异常情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_COST_FLUCTUATION}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_COST_FLUCTUATION}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 转化成本异常
  [ATTENTION_EVENT_TYPE.ACCOUNT_CONVERSION_COST_ABNORMAL]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个账户转化成本异常`,
    desc: '存在多类因素导致波动异常，请及时关注',
    actionText: '查看详情',
    tagText: '平台策略',
    defaultPrompt: '扫描账户转化成本异常情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_CONVERSION_COST_ABNORMAL}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_CONVERSION_COST_ABNORMAL}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 行业热门创意洞察
  [ATTENTION_EVENT_TYPE.INDUSTRY_HOT_CREATIVE_INSIGHTS]: {
    getType: () => 'info',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个行业热门创意洞察`,
    desc: '行业爆款参考，抓抓行业趋势新玩法',
    actionText: '查看详情',
    tagText: '行业洞察',
    defaultPrompt: '扫描行业热门创意洞察情况',
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.INDUSTRY_HOT_CREATIVE_INSIGHTS}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.INDUSTRY_HOT_CREATIVE_INSIGHTS}`,
    enable: (eventData: KeyEventItem) => eventData.count > 0,
  },
  // 账户治理超限额
  [ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_EXCEED_QUOTA]: {
    getType: () => 'error',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个主体触达可投账户数限制`,
    desc: '信誉等级偏低请即时关注',
    actionText: '查看详情',
    tagText: '异动监控',
    defaultPrompt: '帮我查询有触达账户受限的主体',
    enable: (eventData: KeyEventItem) => eventData.count > 0,
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_EXCEED_QUOTA}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_EXCEED_QUOTA}`,
  },
  // 账户治理预警
  [ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_WARNING]: {
    getType: () => 'warning',
    title: (eventData: KeyEventItem) => `${formatCount(eventData.count)} 个主体将触达可投账户数限制`,
    desc: '信誉等级偏低请即时关注',
    actionText: '查看详情',
    tagText: '异动监控',
    defaultPrompt: '帮我查询有触达账户受限的主体',
    enable: (eventData: KeyEventItem) => eventData.count > 0,
    reportDisplayActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DISPLAY}__${ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_WARNING}`,
    reportClickActionName: `${AI_ASSISTANT_REPORT_EVENT_NAME.ATTENTION_CARD_DETAIL_LINK}__${ATTENTION_EVENT_TYPE.ACCOUNT_GOVERNANCE_WARNING}`,
  },
};
